import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  Alert,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { supabase } from '../../api/supabase';
import { useDriverStore } from '../../stores/driverStore';
import { useAuthStore } from '../../stores/authStore';
import theme from '../../theme';

// TypeScript interfaces
interface DriverPayment {
  driverId: string;
  driverName: string;
  amountCash: number;
  amountBank: number;
  notes: string;
}

interface SavedPayment {
  id: string;
  driver_id: string;
  driver_name: string;
  payment_date: string;
  amount_cash: number;
  amount_bank: number;
  total_amount: number;
  payment_method: 'cash' | 'bank' | 'mixed';
  received_by: string;
  notes: string | null;
  created_at: string;
}

interface DailySummary {
  totalCash: number;
  totalBank: number;
  totalPayments: number;
  driverCount: number;
}

export const DailyDriverPayments: React.FC<{ navigation: any }> = ({ navigation }) => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [payments, setPayments] = useState<DriverPayment[]>([]);
  const [savedPayments, setSavedPayments] = useState<SavedPayment[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<DailySummary>({
    totalCash: 0,
    totalBank: 0,
    totalPayments: 0,
    driverCount: 0,
  });

  const drivers = useDriverStore((state) => state.drivers);
  const user = useAuthStore((state) => state.user);

  useEffect(() => {
    loadTodaysPayments();
  }, [selectedDate]);

  useEffect(() => {
    calculateSummary();
  }, [payments, savedPayments]);

  const loadTodaysPayments = async () => {
    try {
      const { data, error } = await supabase
        .from('driver_daily_payments')
        .select('*')
        .eq('payment_date', selectedDate)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setSavedPayments(data || []);
    } catch (error) {
      console.error('Error loading payments:', error);
    }
  };

  const calculateSummary = () => {
    const unsavedCash = payments.reduce((sum, p) => sum + p.amountCash, 0);
    const unsavedBank = payments.reduce((sum, p) => sum + p.amountBank, 0);
    const savedCash = savedPayments.reduce((sum, p) => sum + p.amount_cash, 0);
    const savedBank = savedPayments.reduce((sum, p) => sum + p.amount_bank, 0);

    setSummary({
      totalCash: unsavedCash + savedCash,
      totalBank: unsavedBank + savedBank,
      totalPayments: unsavedCash + unsavedBank + savedCash + savedBank,
      driverCount: payments.length + savedPayments.length,
    });
  };

  const filteredDrivers = drivers.filter(driver =>
    driver.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const addPayment = (driver: any) => {
    // Check if driver already has payment today
    const alreadyPaid = savedPayments.some(p => p.driver_id === driver.id);
    const alreadyInList = payments.some(p => p.driverId === driver.id);

    if (alreadyPaid) {
      Alert.alert('Already Paid', `${driver.name} has already made a payment today.`);
      return;
    }

    if (alreadyInList) {
      Alert.alert('Already Added', `${driver.name} is already in the payment list.`);
      return;
    }

    setPayments([...payments, {
      driverId: driver.id,
      driverName: driver.name,
      amountCash: 0,
      amountBank: 0,
      notes: '',
    }]);
    setSearchQuery('');
  };

  const updatePayment = (driverId: string, field: 'amountCash' | 'amountBank' | 'notes', value: any) => {
    setPayments(payments.map(p =>
      p.driverId === driverId ? { ...p, [field]: value } : p
    ));
  };

  const removePayment = (driverId: string) => {
    setPayments(payments.filter(p => p.driverId !== driverId));
  };

  const saveAllPayments = async () => {
    if (payments.length === 0) {
      Alert.alert('No Payments', 'Please add at least one driver payment.');
      return;
    }

    // Validate all payments have amounts
    const invalidPayments = payments.filter(p => p.amountCash === 0 && p.amountBank === 0);
    if (invalidPayments.length > 0) {
      Alert.alert(
        'Invalid Payments',
        `Please enter payment amounts for: ${invalidPayments.map(p => p.driverName).join(', ')}`
      );
      return;
    }

    setLoading(true);
    try {
      // Prepare payment records
      const paymentRecords = payments.map(p => {
        const totalAmount = p.amountCash + p.amountBank;
        let paymentMethod: 'cash' | 'bank' | 'mixed' = 'cash';
        if (p.amountBank > 0 && p.amountCash === 0) paymentMethod = 'bank';
        else if (p.amountBank > 0 && p.amountCash > 0) paymentMethod = 'mixed';

        return {
          driver_id: p.driverId,
          driver_name: p.driverName,
          payment_date: selectedDate,
          amount_cash: p.amountCash,
          amount_bank: p.amountBank,
          total_amount: totalAmount,
          payment_method: paymentMethod,
          received_by: user?.name || 'Unknown',
          notes: p.notes || null,
        };
      });

      // Insert all payments
      const { error: insertError } = await supabase
        .from('driver_daily_payments')
        .insert(paymentRecords);

      if (insertError) throw insertError;

      // Create company cash transactions for each payment
      const cashTransactions = [];
      
      for (const payment of paymentRecords) {
        // Cash deposit transaction
        if (payment.amount_cash > 0) {
          cashTransactions.push({
            transaction_type: 'cash_deposit',
            category: 'driver_payment',
            amount: payment.amount_cash,
            description: `Cash payment from ${payment.driver_name}`,
            reference_number: payment.driver_id,
            transaction_date: selectedDate,
            verified: true,
            verified_by: user?.name || null,
          });
        }

        // Bank deposit transaction
        if (payment.amount_bank > 0) {
          cashTransactions.push({
            transaction_type: 'bank_deposit',
            category: 'driver_payment',
            amount: payment.amount_bank,
            description: `Bank payment from ${payment.driver_name}`,
            reference_number: payment.driver_id,
            transaction_date: selectedDate,
            verified: true,
            verified_by: user?.name || null,
          });
        }
      }

      // Insert company transactions
      const { error: transactionError } = await supabase
        .from('company_cash_transactions')
        .insert(cashTransactions);

      if (transactionError) throw transactionError;

      Alert.alert(
        'Success',
        `Saved ${payments.length} driver payment(s) successfully!`,
        [{ text: 'OK', onPress: () => {
          setPayments([]);
          loadTodaysPayments();
        }}]
      );
    } catch (error: any) {
      console.error('Error saving payments:', error);
      Alert.alert('Error', `Failed to save payments: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return `${amount.toFixed(2)} AED`;
  };

  const renderSummaryCards = () => (
    <View style={styles.summaryContainer}>
      <Card style={styles.summaryCard}>
        <Ionicons name="cash" size={24} color={theme.colors.success} />
        <Text style={styles.summaryValue}>{formatCurrency(summary.totalCash)}</Text>
        <Text style={styles.summaryLabel}>Cash</Text>
      </Card>

      <Card style={styles.summaryCard}>
        <Ionicons name="card" size={24} color={theme.colors.primary} />
        <Text style={styles.summaryValue}>{formatCurrency(summary.totalBank)}</Text>
        <Text style={styles.summaryLabel}>Bank</Text>
      </Card>

      <Card style={styles.summaryCard}>
        <Ionicons name="wallet" size={24} color={theme.colors.warning} />
        <Text style={styles.summaryValue}>{formatCurrency(summary.totalPayments)}</Text>
        <Text style={styles.summaryLabel}>Total</Text>
      </Card>

      <Card style={styles.summaryCard}>
        <Ionicons name="people" size={24} color={theme.colors.textSecondary} />
        <Text style={styles.summaryValue}>{summary.driverCount}</Text>
        <Text style={styles.summaryLabel}>Drivers</Text>
      </Card>
    </View>
  );

  const renderPaymentForm = (payment: DriverPayment) => (
    <Card key={payment.driverId} style={styles.paymentCard}>
      <View style={styles.paymentHeader}>
        <View style={styles.driverInfo}>
          <Ionicons name="person" size={20} color={theme.colors.primary} />
          <Text style={styles.driverName}>{payment.driverName}</Text>
        </View>
        <TouchableOpacity onPress={() => removePayment(payment.driverId)}>
          <Ionicons name="close-circle" size={24} color={theme.colors.error} />
        </TouchableOpacity>
      </View>

      <View style={styles.amountInputs}>
        <View style={styles.inputGroup}>
          <Text style={styles.inputLabel}>Cash Amount (AED)</Text>
          <View style={styles.inputContainer}>
            <Ionicons name="cash" size={20} color={theme.colors.success} />
            <TextInput
              style={styles.input}
              placeholder="0.00"
              keyboardType="decimal-pad"
              value={payment.amountCash > 0 ? payment.amountCash.toString() : ''}
              onChangeText={(text) => updatePayment(payment.driverId, 'amountCash', parseFloat(text) || 0)}
            />
          </View>
        </View>

        <View style={styles.inputGroup}>
          <Text style={styles.inputLabel}>Bank Amount (AED)</Text>
          <View style={styles.inputContainer}>
            <Ionicons name="card" size={20} color={theme.colors.primary} />
            <TextInput
              style={styles.input}
              placeholder="0.00"
              keyboardType="decimal-pad"
              value={payment.amountBank > 0 ? payment.amountBank.toString() : ''}
              onChangeText={(text) => updatePayment(payment.driverId, 'amountBank', parseFloat(text) || 0)}
            />
          </View>
        </View>
      </View>

      <View style={styles.totalRow}>
        <Text style={styles.totalLabel}>Total:</Text>
        <Text style={styles.totalValue}>
          {formatCurrency(payment.amountCash + payment.amountBank)}
        </Text>
      </View>

      <TextInput
        style={styles.notesInput}
        placeholder="Notes (optional)"
        multiline
        numberOfLines={2}
        value={payment.notes}
        onChangeText={(text) => updatePayment(payment.driverId, 'notes', text)}
      />
    </Card>
  );

  const renderSavedPayment = (payment: SavedPayment) => (
    <Card key={payment.id} style={styles.savedPaymentCard}>
      <View style={styles.savedPaymentHeader}>
        <View style={styles.driverInfo}>
          <Ionicons name="checkmark-circle" size={20} color={theme.colors.success} />
          <Text style={styles.driverName}>{payment.driver_name}</Text>
        </View>
        <View style={[styles.methodBadge, { backgroundColor: getMethodColor(payment.payment_method) + '20' }]}>
          <Text style={[styles.methodText, { color: getMethodColor(payment.payment_method) }]}>
            {payment.payment_method.toUpperCase()}
          </Text>
        </View>
      </View>

      <View style={styles.savedPaymentDetails}>
        {payment.amount_cash > 0 && (
          <View style={styles.amountRow}>
            <Text style={styles.amountLabel}>Cash:</Text>
            <Text style={styles.amountValue}>{formatCurrency(payment.amount_cash)}</Text>
          </View>
        )}
        {payment.amount_bank > 0 && (
          <View style={styles.amountRow}>
            <Text style={styles.amountLabel}>Bank:</Text>
            <Text style={styles.amountValue}>{formatCurrency(payment.amount_bank)}</Text>
          </View>
        )}
        <View style={styles.amountRow}>
          <Text style={styles.totalLabel}>Total:</Text>
          <Text style={styles.totalValue}>{formatCurrency(payment.total_amount)}</Text>
        </View>
        <View style={styles.receivedByRow}>
          <Text style={styles.receivedByLabel}>Received by: {payment.received_by}</Text>
        </View>
        {payment.notes && (
          <Text style={styles.savedNotes}>{payment.notes}</Text>
        )}
      </View>
    </Card>
  );

  const getMethodColor = (method: string) => {
    switch (method) {
      case 'cash': return theme.colors.success;
      case 'bank': return theme.colors.primary;
      case 'mixed': return theme.colors.warning;
      default: return theme.colors.textSecondary;
    }
  };

  return (
    <ScrollView style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>Daily Driver Payments</Text>
        <Text style={styles.subtitle}>Record cash collections from drivers</Text>
      </View>

      {/* Date Selector */}
      <Card style={styles.dateCard}>
        <Text style={styles.dateLabel}>Payment Date:</Text>
        <TextInput
          style={styles.dateInput}
          value={selectedDate}
          onChangeText={setSelectedDate}
          placeholder="YYYY-MM-DD"
        />
      </Card>

      {renderSummaryCards()}

      {/* Add Driver Section */}
      <Card style={styles.addDriverCard}>
        <Text style={styles.sectionTitle}>Add Driver Payment</Text>
        <View style={styles.searchContainer}>
          <Ionicons name="search" size={20} color={theme.colors.textSecondary} />
          <TextInput
            style={styles.searchInput}
            placeholder="Search driver by name..."
            value={searchQuery}
            onChangeText={setSearchQuery}
          />
        </View>

        {searchQuery.length > 0 && (
          <ScrollView style={styles.driverList} nestedScrollEnabled>
            {filteredDrivers.slice(0, 5).map(driver => (
              <TouchableOpacity
                key={driver.id}
                style={styles.driverItem}
                onPress={() => addPayment(driver)}
              >
                <Ionicons name="person-add" size={20} color={theme.colors.primary} />
                <Text style={styles.driverItemText}>{driver.name}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        )}
      </Card>

      {/* Payment Forms */}
      {payments.length > 0 && (
        <View style={styles.paymentsSection}>
          <Text style={styles.sectionTitle}>Today's Payments ({payments.length})</Text>
          {payments.map(renderPaymentForm)}

          <Button
            title={loading ? 'Saving...' : `Save ${payments.length} Payment(s)`}
            onPress={saveAllPayments}
            disabled={loading}
            style={styles.saveButton}
          />
        </View>
      )}

      {/* Saved Payments */}
      {savedPayments.length > 0 && (
        <View style={styles.savedSection}>
          <Text style={styles.sectionTitle}>Saved Payments ({savedPayments.length})</Text>
          {savedPayments.map(renderSavedPayment)}
        </View>
      )}

      {payments.length === 0 && savedPayments.length === 0 && (
        <Card style={styles.emptyCard}>
          <Ionicons name="wallet-outline" size={48} color={theme.colors.textSecondary} />
          <Text style={styles.emptyText}>No payments recorded for {selectedDate}</Text>
          <Text style={styles.emptySubtext}>Search for a driver to add a payment</Text>
        </Card>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  header: {
    padding: 16,
    backgroundColor: theme.colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: theme.colors.textPrimary,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: theme.colors.textSecondary,
  },
  dateCard: {
    margin: 16,
    padding: 16,
  },
  dateLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    marginBottom: 8,
  },
  dateInput: {
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    color: theme.colors.textPrimary,
    backgroundColor: theme.colors.background,
  },
  summaryContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingHorizontal: 12,
    gap: 12,
    marginBottom: 16,
  },
  summaryCard: {
    flex: 1,
    minWidth: Platform.OS === 'web' ? 150 : '45%',
    padding: 16,
    alignItems: 'center',
  },
  summaryValue: {
    fontSize: 20,
    fontWeight: 'bold',
    color: theme.colors.textPrimary,
    marginTop: 8,
  },
  summaryLabel: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    marginTop: 4,
  },
  addDriverCard: {
    margin: 16,
    padding: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    marginBottom: 12,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderRadius: 8,
    padding: 12,
    backgroundColor: theme.colors.background,
  },
  searchInput: {
    flex: 1,
    marginLeft: 8,
    fontSize: 16,
    color: theme.colors.textPrimary,
  },
  driverList: {
    maxHeight: 200,
    marginTop: 8,
  },
  driverItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
    gap: 12,
  },
  driverItemText: {
    fontSize: 16,
    color: theme.colors.textPrimary,
  },
  paymentsSection: {
    padding: 16,
  },
  paymentCard: {
    marginBottom: 12,
    padding: 16,
  },
  paymentHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  driverInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  driverName: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.textPrimary,
  },
  amountInputs: {
    gap: 12,
    marginBottom: 12,
  },
  inputGroup: {
    gap: 8,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.colors.textSecondary,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderRadius: 8,
    padding: 12,
    backgroundColor: theme.colors.background,
    gap: 8,
  },
  input: {
    flex: 1,
    fontSize: 16,
    color: theme.colors.textPrimary,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: theme.colors.border,
    marginBottom: 12,
  },
  totalLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.textPrimary,
  },
  totalValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.primary,
  },
  notesInput: {
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    color: theme.colors.textPrimary,
    backgroundColor: theme.colors.background,
    minHeight: 60,
    textAlignVertical: 'top',
  },
  saveButton: {
    marginTop: 16,
  },
  savedSection: {
    padding: 16,
  },
  savedPaymentCard: {
    marginBottom: 12,
    padding: 16,
    backgroundColor: theme.colors.success + '05',
    borderLeftWidth: 4,
    borderLeftColor: theme.colors.success,
  },
  savedPaymentHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  methodBadge: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
  },
  methodText: {
    fontSize: 12,
    fontWeight: '600',
  },
  savedPaymentDetails: {
    gap: 8,
  },
  amountRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  amountLabel: {
    fontSize: 14,
    color: theme.colors.textSecondary,
  },
  amountValue: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.colors.textPrimary,
  },
  receivedByRow: {
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: theme.colors.border,
  },
  receivedByLabel: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    fontStyle: 'italic',
  },
  savedNotes: {
    fontSize: 14,
    color: theme.colors.textPrimary,
    fontStyle: 'italic',
    marginTop: 4,
    padding: 8,
    backgroundColor: theme.colors.background,
    borderRadius: 4,
  },
  emptyCard: {
    margin: 16,
    padding: 32,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    marginTop: 16,
  },
  emptySubtext: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    marginTop: 4,
  },
});
